/**
 * A PRÉVIA DA GERAÇÃO no modal "Nova fatura" (pedido do Heitor de 03/11/2026).
 *
 * O caso: Sorriso, assinatura 114, fatura de 11/2026. O modal propôs 01/11 a 30/11 e R$ 6.371,79 com os dois itens
 * cheios; o certo era 01/10 a 31/10 e R$ 5.372,79. Dois erros, os dois por o modal ter a própria regra:
 * - o período abria no mês corrente, e órgão público é postecipado (a fatura de N cobre o serviço de N-1);
 * - os itens eram copiados cheios do contrato, e o aditivo de R$ 999,00 só existe a partir de 03/11.
 *
 * ⛔ A tela NÃO tem regra de período nem de vigência. Quem decide é a geração mensal do backend, e o modal lê a prévia
 * dela (`GET /invoices/generation-preview`): o mesmo código monta a fatura gerada e a proposta do modal, então as duas
 * não têm como discordar. Aqui só mora a leitura da resposta e o texto que a linha mostra ao operador.
 */
import { fmtCompetencia, fmtDate } from './formatters';

export type RegraDoPeriodo = 'ciclo' | 'orgao-publico' | 'mes-corrente';

/**
 * A linha que cobra parte do período. ⚠️ Chama-se recorte, e não vigência: o pró-rata de REAJUSTE também parte a
 * linha, e aí os dias não vêm da vigência do item. Por isso a tela não monta a frase a partir de `de`/`ate` (escreveria
 * "entra em" sobre uma fatia de reajuste): ela mostra a `memoria`, escrita pelo servidor, que sabe a causa.
 */
export interface RecorteDaLinha {
  dias: number;
  diasTotal: number;
  de: string;
  ate: string;
  /** Só para pílula ou ícone. ⛔ Frase a partir dela recria a frase na tela: a frase é `memoria`. */
  causa?: 'vigencia' | 'reajuste' | 'vigencia-e-reajuste' | 'indeterminada';
  memoria: string;
}

export interface LinhaDaPrevia {
  name: string;
  productId?: number | null;
  productCode?: string | null;
  description?: string | null;
  type?: string;
  quantity: number;
  unitValue: number;
  totalValue: number;
  period?: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  /** O valor cheio do mês, quando a linha foi recortada. */
  baseValue?: number | null;
  locationId?: number | null;
  locationName?: string | null;
  /** Presente só quando a linha cobra parte do período. */
  recorte?: RecorteDaLinha | null;
}

export interface ItemForaDoPeriodo {
  name: string;
  productId?: number | null;
  /** O valor cheio do mês, que esta fatura NÃO cobra. */
  totalValue: number;
  vigenciaInicio?: string | null;
  vigenciaFim?: string | null;
  motivo: 'ainda-nao-comecou' | 'ja-terminou';
  /** A primeira fatura que cobra o item ('YYYY-MM'); `null` quando já terminou. */
  competenciaQueCobra?: string | null;
}

/** A fatura ativa que a competência já tem: a geração recusaria, e a prévia diz qual é. */
export interface FaturaExistente {
  id: number;
  code: string;
  status: string;
}

export interface PreviaDaGeracao {
  referenceMonth: string;
  periodStart: string;
  periodEnd: string;
  regraDoPeriodo: RegraDoPeriodo;
  items: LinhaDaPrevia[];
  foraDoPeriodo: ItemForaDoPeriodo[];
  avisos: string[];
  psPendentes: number;
  faturaExistente: FaturaExistente | null;
}

const DIA = /^\d{4}-\d{2}-\d{2}$/;
const MES = /^\d{4}-\d{2}$/;
const numero = (v: any) => typeof v === 'number' && Number.isFinite(v);

/**
 * A resposta tem a forma do contrato? Fora dela, `null`: a tela diz que não leu, e não preenche nada. Preencher com
 * metade da resposta seria o defeito de volta, com cara de conferido.
 */
export function previaDaGeracaoValida(r: any): PreviaDaGeracao | null {
  if (!r || typeof r !== 'object') return null;
  if (!MES.test(String(r.referenceMonth)) || !DIA.test(String(r.periodStart)) || !DIA.test(String(r.periodEnd))) return null;
  if (!Array.isArray(r.items) || !r.items.every((i: any) => i && typeof i.name === 'string' && numero(i.totalValue))) return null;
  const fora = Array.isArray(r.foraDoPeriodo) ? r.foraDoPeriodo : [];
  if (!fora.every((f: any) => f && typeof f.name === 'string' && numero(f.totalValue))) return null;
  return {
    referenceMonth: r.referenceMonth,
    periodStart: r.periodStart,
    periodEnd: r.periodEnd,
    regraDoPeriodo: r.regraDoPeriodo,
    items: r.items,
    foraDoPeriodo: fora,
    avisos: Array.isArray(r.avisos) ? r.avisos.filter((a: any) => typeof a === 'string') : [],
    psPendentes: numero(r.psPendentes) ? r.psPendentes : 0,
    faturaExistente: r.faturaExistente && typeof r.faturaExistente.code === 'string' ? r.faturaExistente : null,
  };
}

/** A frase sob o período, que diz de onde ele veio. */
export function rotuloDaRegra(regra: RegraDoPeriodo | undefined): string | null {
  if (regra === 'orgao-publico') return 'órgão público fatura o mês anterior';
  if (regra === 'ciclo') return 'ciclo de faturamento do cliente';
  if (regra === 'mes-corrente') return 'o próprio mês';
  return null;
}

/** O motivo na linha recortada, como o servidor escreveu. Linha inteira não tem motivo. */
export function motivoDoRecorte(r: RecorteDaLinha | null | undefined): string | null {
  if (!r || r.dias >= r.diasTotal) return null;
  return typeof r.memoria === 'string' && r.memoria.trim() ? r.memoria.trim() : `cobrado por ${r.dias} de ${r.diasTotal} dias do período`;
}

/** O motivo do item que ficou fora: "entra em 03/11/2026, vai na fatura 12/2026". */
export function motivoForaDoPeriodo(f: ItemForaDoPeriodo): string {
  if (f.motivo === 'ainda-nao-comecou') {
    const entra = f.vigenciaInicio ? `entra em ${fmtDate(f.vigenciaInicio)}` : 'ainda não começou';
    return f.competenciaQueCobra ? `${entra}, vai na fatura ${fmtCompetencia(f.competenciaQueCobra)}` : entra;
  }
  return f.vigenciaFim ? `terminou em ${fmtDate(f.vigenciaFim)}, não é mais cobrado` : 'já terminou, não é mais cobrado';
}

/** As linhas da prévia no formato do formulário do modal (o `baseValue` é o que o "Vlr Base" mostra). */
export function itensDoFormulario(p: PreviaDaGeracao): any[] {
  return p.items.map((i) => ({
    productId: i.productId ?? undefined,
    productCode: i.productCode ?? undefined,
    name: i.name,
    description: i.description ?? undefined,
    type: i.type,
    quantity: i.quantity,
    unitValue: i.unitValue,
    totalValue: i.totalValue,
    baseValue: i.baseValue ?? i.unitValue,
    period: i.period,
    periodStart: i.periodStart || p.periodStart,
    periodEnd: i.periodEnd || p.periodEnd,
    locationId: i.locationId ?? undefined,
    locationName: i.locationName ?? undefined,
    motivoDaVigencia: motivoDoRecorte(i.recorte),
  }));
}
